import React, { useEffect, useState } from 'react';
import { Crown, Trophy, Lock, TrendingUp, Heart, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../App';
import { Button } from '../ui/button';
import { trackUpgradeEvent } from '../../utils/upgradeTracker';

function formatRelativeDate(dateStr) {
  if (!dateStr) return ',';
  try {
    const date = new Date(dateStr);
    const diffDays = Math.floor((Date.now() - date.getTime()) / 86400000);
    if (diffDays <= 0) return 'today';
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 30) return `${diffDays} days ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} mo ago`;
    return `${Math.floor(diffDays / 365)} yr ago`;
  } catch { return dateStr; }
}

export function TopClientsCard({ onUpgrade }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingFor, setSendingFor] = useState(null);   // client_id currently being thanked
  const [sentFor, setSentFor] = useState({});            // client_id -> discount_code

  useEffect(() => {
    api.get('/providers/me/top-clients')
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const handleSendThankYou = async (client) => {
    if (client.is_guest) {
      toast.info('Guest clients can\'t receive in-app messages');
      return;
    }
    setSendingFor(client.client_id);
    try {
      const res = await api.post(`/providers/me/top-clients/${client.client_id}/thank-you`);
      setSentFor(prev => ({ ...prev, [client.client_id]: res.data.discount_code }));
      toast.success(
        res.data.reused
          ? `Reused recent thank-you, code: ${res.data.discount_code}`
          : `Thanks sent to ${client.name}, code: ${res.data.discount_code}`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send thank-you');
    } finally {
      setSendingFor(null);
    }
  };

  // Funnel: log a single impression when the locked state first renders
  // for this mount. useEffect runs unconditionally (hook order stable)
  // and gates on the data we've already fetched.
  useEffect(() => {
    if (data && data.locked) {
      trackUpgradeEvent('impression', 'top_clients_card');
    }
  }, [data]);

  if (loading || !data) return null;

  const { is_pro, locked, repeat_client_count, total_repeat_revenue, clients } = data;

  // ── Locked (Free tier) state ──
  if (locked) {
    return (
      <div
        className="card-surface p-6 relative overflow-hidden border border-[#D4AF37]/30"
        data-testid="top-clients-card-locked"
      >
        <div className="flex items-start justify-between mb-4 gap-4">
          <div className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-[#D4AF37]" />
            <h3 className="text-sm font-medium text-[#1A1A1A]">Your Top 5 Clients</h3>
            <span className="inline-flex items-center gap-1 bg-[#1A1A1A] text-[#D4AF37] text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider">
              <Crown className="h-3 w-3" /> Pro
            </span>
          </div>
        </div>

        {/* Teaser stats (these we show regardless of tier) */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="bg-[#F9F6F0] rounded-lg p-3">
            <p className="text-[10px] font-semibold tracking-widest uppercase text-stone-500">Repeat clients</p>
            <p className="font-heading text-2xl text-[#1A1A1A] mt-1">{repeat_client_count}</p>
          </div>
          <div className="bg-[#F9F6F0] rounded-lg p-3">
            <p className="text-[10px] font-semibold tracking-widest uppercase text-stone-500">Repeat revenue</p>
            <p className="font-heading text-2xl text-[#1A1A1A] mt-1">&pound;{total_repeat_revenue.toFixed(0)}</p>
          </div>
        </div>

        {/* Blurred preview rows */}
        <div className="relative">
          <div className="space-y-2 filter blur-sm select-none" aria-hidden="true">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="flex items-center justify-between py-3 px-3 bg-stone-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#D4AF37]" />
                  <div>
                    <p className="h-3 w-28 bg-stone-300 rounded mb-1.5" />
                    <p className="h-2 w-20 bg-stone-200 rounded" />
                  </div>
                </div>
                <p className="h-4 w-14 bg-[#D4AF37]/50 rounded" />
              </div>
            ))}
          </div>

          {/* Overlay */}
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-t from-white via-white/95 to-white/70">
            <div className="w-12 h-12 rounded-full bg-[#1A1A1A] flex items-center justify-center mb-3 shadow-lg">
              <Lock className="h-5 w-5 text-[#D4AF37]" />
            </div>
            <p className="text-[#1A1A1A] font-semibold text-sm mb-1">See who's driving your revenue</p>
            <p className="text-xs text-stone-500 mb-4 text-center max-w-xs">
              See the exact clients worth nurturing to lift repeat bookings.
            </p>
            <Button
              onClick={onUpgrade}
              className="btn-primary gap-2"
              data-testid="unlock-top-clients-btn"
            >
              <Crown className="h-4 w-4" />
              Upgrade to Pro
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Pro state ──
  if (!clients.length) {
    return (
      <div className="card-surface p-6" data-testid="top-clients-card-empty">
        <div className="flex items-center gap-2 mb-2">
          <Trophy className="h-5 w-5 text-[#D4AF37]" />
          <h3 className="text-sm font-medium text-[#1A1A1A]">Your Top 5 Clients</h3>
        </div>
        <p className="text-xs text-stone-500">
          Once clients start rebooking, your VIPs will appear here with lifetime revenue and booking count.
        </p>
      </div>
    );
  }

  return (
    <div className="card-surface p-6" data-testid="top-clients-card">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Trophy className="h-5 w-5 text-[#D4AF37]" />
            <h3 className="text-sm font-medium text-[#1A1A1A]">Your Top 5 Clients</h3>
          </div>
          <p className="text-xs text-stone-400">Nurture these VIPs, every repeat booking earns <strong className="text-[#1A1A1A]">0% commission</strong>.</p>
        </div>
        <div className="hidden sm:flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">
          <TrendingUp className="h-3 w-3" />
          {repeat_client_count} repeat {repeat_client_count === 1 ? 'client' : 'clients'}
        </div>
      </div>

      <div className="space-y-1">
        {clients.map((c, i) => {
          const sent = sentFor[c.client_id];
          const sending = sendingFor === c.client_id;
          return (
            <div
              key={`${c.client_id}-${i}`}
              className="flex items-center justify-between py-3 px-3 hover:bg-[#F9F6F0] rounded-lg transition-colors group"
              data-testid={`top-client-row-${i}`}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F5D56A] to-[#D4AF37] text-white font-heading text-sm flex items-center justify-center overflow-hidden flex-shrink-0">
                    {c.picture
                      ? <img src={c.picture} alt={c.name} className="w-full h-full object-cover" />
                      : (c.name || 'C').charAt(0).toUpperCase()}
                  </div>
                  {i === 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#1A1A1A] text-[#D4AF37] text-[10px] font-bold flex items-center justify-center shadow">
                      1
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[#1A1A1A] truncate">
                    {c.name}
                    {c.is_guest && <span className="text-[10px] text-stone-400 ml-1.5 uppercase tracking-wider">Guest</span>}
                  </p>
                  <p className="text-xs text-stone-400">
                    {c.booking_count} bookings &middot; last {formatRelativeDate(c.last_booking_date)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 ml-3">
                {!c.is_guest && (
                  sent ? (
                    <span
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1.5 rounded-full"
                      data-testid={`vip-thanks-sent-${i}`}
                      title={`Code: ${sent}`}
                    >
                      <Check className="h-3.5 w-3.5" />
                      Sent
                    </span>
                  ) : (
                    <Button
                      onClick={() => handleSendThankYou(c)}
                      disabled={sending}
                      variant="outline"
                      size="sm"
                      className="border-[#D4AF37]/40 text-[#1A1A1A] hover:bg-[#D4AF37] hover:text-white hover:border-[#D4AF37] transition-colors gap-1.5 text-xs opacity-0 group-hover:opacity-100 focus:opacity-100"
                      data-testid={`vip-thanks-btn-${i}`}
                      title="Send a thank-you with a 10% discount code"
                    >
                      {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Heart className="h-3.5 w-3.5" />}
                      {sending ? 'Sending' : 'Send thanks'}
                    </Button>
                  )
                )}

                <div className="text-right">
                  <p className="text-sm font-semibold text-[#1A1A1A]">&pound;{c.lifetime_value.toFixed(2)}</p>
                  <p className="text-[10px] text-stone-400 uppercase tracking-wider">lifetime</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-4 border-t border-stone-100 flex items-center justify-between text-xs text-stone-500">
        <span>{repeat_client_count} total repeat {repeat_client_count === 1 ? 'client' : 'clients'}</span>
        <span>Combined lifetime revenue: <strong className="text-[#1A1A1A]">&pound;{total_repeat_revenue.toFixed(2)}</strong></span>
      </div>
    </div>
  );
}
